<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm']);

const { t } = useI18n();
const dialogRef = ref(null);
const isEditMode = ref(false);

const form = reactive({ id: null, name: '' });

const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  form.id = null;
  form.name = '';
};

const open = (pipeline = null) => {
  isEditMode.value = !!pipeline;
  form.id = pipeline?.id || null;
  form.name = pipeline?.name || '';
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;
  emit('confirm', { id: form.id, name: form.name.trim() });
};

const closeDialog = () => dialogRef.value?.close();

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ dialogRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditMode
        ? t('KANBAN.PIPELINES.EDIT.TITLE')
        : t('KANBAN.PIPELINES.ADD.TITLE')
    "
    width="md"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <Input
      v-model="form.name"
      :label="t('KANBAN.PIPELINES.ADD.NAME_LABEL')"
      :placeholder="t('KANBAN.PIPELINES.ADD.NAME_PLACEHOLDER')"
      :disabled="isLoading"
      autofocus
    />

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="
            isEditMode
              ? t('KANBAN.PIPELINES.EDIT.SUBMIT')
              : t('KANBAN.PIPELINES.ADD.SUBMIT')
          "
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
